import { useCallback, useEffect, useState } from 'react'
import useWebSocket from 'react-use-websocket'

import crossFetch from 'cross-fetch'

import { resources } from 'config'
import { useCart } from 'public/components/online_ordering/CartContext'

import * as VoiceAI from './VoiceAI.types'

const voiceAIHttpUrl = resources.apiHost + '/voice-ai'
const voiceAIWebSocketUrl = resources.wssHost + '/voice-ai'

export const useVoiceAI = (restaurantGuid: string) => {
  const [callId, setCallId] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const { cartGuid, refetchCart, replaceCart } = useCart()

  const { sendJsonMessage, lastJsonMessage, getWebSocket } = useWebSocket(
    `${voiceAIWebSocketUrl}/text`,
    {
      share: false,
      onClose: () => {
        console.log('WebSocket connection closed')
        setIsConnected(false)
      },
      onError: (error) => {
        console.error('WebSocket error:', error)
        setIsConnected(false)
      },
      onOpen: () => {
        console.log('WebSocket connection established')
        setIsConnected(true)
      },
      shouldReconnect: () => false
    }
  )

  useEffect(() => {
    return () => {
      getWebSocket()?.close()
      setCallId(null)
      setIsConnected(false)
    }
  }, [getWebSocket])

  useEffect(() => {
    if (lastJsonMessage && (lastJsonMessage as any).event === 'state') {
      const stateMessage = lastJsonMessage as VoiceAI.StateMessage

      if (
        stateMessage.state?.updatedEntities?.includes('cart') &&
        stateMessage.state.cartGuid
      ) {
        const newCartGuid = stateMessage.state.cartGuid
        if (newCartGuid !== cartGuid) {
          replaceCart(newCartGuid)
        } else {
          refetchCart({ guid: newCartGuid })
        }
      }
    }
  }, [lastJsonMessage, replaceCart, refetchCart, cartGuid])

  useEffect(() => {
    if (callId) {
      sendJsonMessage({
        event: 'start',
        callId
      } as VoiceAI.StartMessage)
    }
  }, [callId, sendJsonMessage])

  const sendText = useCallback(
    (text: string) => {
      if (isConnected) {
        sendJsonMessage({
          event: 'text',
          text: { content: text }
        } as VoiceAI.TextMessage)
      } else {
        console.warn('WebSocket is not connected. Cannot send text:', text)
      }
    },
    [isConnected, sendJsonMessage]
  )

  useEffect(() => {
    const abort = new AbortController()
    let cancelled = false

    const initSession = async () => {
      const sessionResponse = await crossFetch(`${voiceAIHttpUrl}/session`, {
        method: 'POST',
        body: JSON.stringify({
          restaurantGuid,
          cartGuid,
          callSource: 'CONVERSATIONAL_ORDERING'
        }),
        signal: abort.signal
      })

      if (cancelled) {
        return
      }

      if (!sessionResponse.ok) {
        throw new Error(
          `Failed to initialize Voice AI session: ${sessionResponse.statusText}`
        )
      }

      const sessionData = await sessionResponse.json()
      const callId = sessionData.callId
      setCallId(callId)
    }

    if (restaurantGuid) {
      initSession().catch((error) => {
        if (!cancelled) {
          console.error('Error initializing Voice AI session:', error)
        }

        setIsConnected(false)
      })
    }

    return () => {
      cancelled = true
      abort.abort()
    }
  }, [cartGuid, restaurantGuid])

  return { isConnected, lastJsonMessage: lastJsonMessage as any, sendText }
}
